import { Fragment, useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';

// components
import { MobileModal } from '../../global/MobileModal/MobileModal';
import { sbMobileRefineMenuFiltersMap } from '../../finding/SbMobileRefineMenu/SbMobileRefineMenuFilters/sbMobileRefineMenuFiltersMap';
import { BuyMobileNavRefineMenuFilters } from './BuyMobileNavRefineMenuFilters';
import { BuyMobuileRefineMenuSortBy } from './BuyMobuileRefineMenuSortBy';
import { SbMobileRefineMenuHeader } from '../../finding/SbMobileRefineMenu/SbMobileRefineMenuHeader/SbMobileRefineMenuHeader';

import { SbMobileAppliedFilters } from '../../finding/SbMobileAppliedFilters/SbMobileAppliedFilters';
import { FullPageWidth } from '../../components/global/FullPageWidth/FullPageWidth';
import Filter from 'dibs-icons/exports/Filter';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { getValidMobileAppliedFilters } from '../../finding/SbMobileRefineMenu/SbMobileRefineMenuFilters/sbMobileRefineMenuFiltersMap';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// actions
import { toggleFilterDisplay } from '../../actions/filterActions';

// helpers
import { trackEvent } from 'dibs-tracking';
import { useIntl } from 'dibs-react-intl';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import get from 'lodash.get';
import { getQueryParam } from '../../utils/uriUtils';

// styles
import styles from '../../finding/SbMobileRefineMenu/SbMobileRefineMenu.scss';
import sbMobileSortRefineStyles from '../../finding/SbMobileSortRefine/SbMobileSortRefine.scss';
import { SbMobileSortRefineMessages as messages } from '../../finding/SbMobileSortRefine/SbMobileSortRefineMessages';

function BuyMobileNavComponent(props) {
    const {
        user,
        shown,
        viewer,
        filters,
        buyPage: itemSearch,
        uriRef,
        isClient,
        closeFilterDisplay,
        openFilterDisplay,
        hasFilterUpdates,
    } = props;

    const intl = useIntl();

    const topSentinelRef = useRef(null);
    const wrapperRef = useRef(null);
    const prevScroll = useRef(0);

    const [isScrollUp, setIsScrollUp] = useState(false);
    const [isHeaderSticking, setIsHeaderSticking] = useState(false);

    const { totalResults, appliedFilters = [] } = itemSearch;
    const filtersCount = getValidMobileAppliedFilters({
        appliedFilters,
    }).reduce((prev, next) => next.values.length + prev, 0);
    const refinedResultsCountText = filtersCount ? ` (${filtersCount})` : '';

    const handleRefineMenuClose = useCallback(() => {
        closeFilterDisplay();
    }, [closeFilterDisplay]);

    function handleRefineClick() {
        trackEvent({
            action: 'refine link clicked',
            category: 'results page facet selected',
            label: 'buy page',
        });
        openFilterDisplay();
    }

    const scrollListener = useCallback(
        debounce(() => {
            const scrollY = window.scrollY;
            if (scrollY === prevScroll.current) {
                return;
            } else {
                setIsScrollUp(scrollY > prevScroll.current);
            }
            prevScroll.current = scrollY;
        }, 55),
        [isScrollUp, setIsScrollUp, prevScroll]
    );

    useEffect(() => {
        window.addEventListener('scroll', scrollListener);
        return () => {
            window.removeEventListener('scroll', scrollListener);
        };
    }, [scrollListener]);

    useEffect(() => {
        if (getQueryParam(get(global, 'location.search'), 'modal') === 'refine') {
            openFilterDisplay();
        }
    }, [openFilterDisplay]);

    return (
        <>
            <MobileModal
                isOpen={shown}
                handleCloseClick={handleRefineMenuClose}
                usePortal={false}
                hideCloseButton
            >
                <CurrencyProvider
                    render={({ currency }) => (
                        <>
                            <Fragment>
                                <SbMobileRefineMenuHeader
                                    totalResultsCount={totalResults}
                                    hideRefineMenu={handleRefineMenuClose}
                                    hasFilterUpdates={hasFilterUpdates}
                                />
                                <FullPageWidth>
                                    <SbMobileAppliedFilters
                                        user={user}
                                        itemSearch={itemSearch}
                                        currency={currency}
                                    />
                                </FullPageWidth>
                            </Fragment>
                            <div className={styles.separator} />
                            <BuyMobuileRefineMenuSortBy itemSearch={itemSearch} />
                            <BuyMobileNavRefineMenuFilters
                                user={user}
                                viewer={viewer}
                                filters={filters}
                                currency={currency}
                                itemSearch={itemSearch}
                                filterMap={sbMobileRefineMenuFiltersMap}
                                initiallyExpanded={filterName => filterName.includes('category')}
                                uriRef={uriRef}
                                isClient={isClient}
                            />
                        </>
                    )}
                />
            </MobileModal>
            <>
                <div className={styles.topSentinel} ref={topSentinelRef} />
                <VisibilityTracker
                    elementRef={topSentinelRef}
                    onVisibilityChange={({ isVisible }) => setIsHeaderSticking(!isVisible)}
                    watchAfterFirstVisible
                />
                <div
                    className={classnames(sbMobileSortRefineStyles.wrapper, {
                        [sbMobileSortRefineStyles.stickyMode]: isHeaderSticking,
                        [sbMobileSortRefineStyles.hideMode]: isScrollUp,
                    })}
                    onClick={() => {
                        if (isHeaderSticking) {
                            const searchResults = document.querySelector(
                                "div[data-tn='search-results-container']"
                            );
                            const header = document.getElementById('js-header');
                            window.scroll({
                                top:
                                    get(searchResults, 'offsetTop', 0) + // distance search results is from top of container
                                    get(header, 'clientHeight', 0) - // account for the height of the mobile nav
                                    get(wrapperRef, 'current.clientHeight', 0), // as well as the height of the filter/results header (to keep it in view)
                                behavior: 'smooth',
                            });
                        }
                    }}
                    ref={wrapperRef}
                >
                    <div className={sbMobileSortRefineStyles.resultsCount}>
                        {intl.formatMessage(messages.totalResults, {
                            resultCount: totalResults || 0,
                        })}
                    </div>
                    <div className={sbMobileSortRefineStyles.cell}>
                        <button
                            className={sbMobileSortRefineStyles.button}
                            onClick={handleRefineClick}
                            data-tn="refine"
                        >
                            <Filter className={sbMobileSortRefineStyles.filterIcon} />
                            {`${intl.formatMessage(
                                messages.sortAndRefine
                            )}${refinedResultsCountText}`}
                        </button>
                    </div>
                </div>
            </>
        </>
    );
}

BuyMobileNavComponent.defaultProps = {
    closeFilterDisplay: () => {},
    openFilterDisplay: () => {},
    hasFilterUpdates: false,
};

BuyMobileNavComponent.propTypes = {
    isClient: PropTypes.bool,
    viewer: PropTypes.object.isRequired,
    filters: PropTypes.array.isRequired,
    buyPage: PropTypes.object.isRequired,
    user: PropTypes.object,
    uriRef: PropTypes.string,

    // redux
    openFilterDisplay: PropTypes.func.isRequired,
    closeFilterDisplay: PropTypes.func.isRequired,
    shown: PropTypes.bool.isRequired,
    hasFilterUpdates: PropTypes.bool.isRequired,
};

BuyMobileNavComponent = connect(
    ({ filters }) => ({
        shown: filters.filtersShown,
        hasFilterUpdates: filters.hasNewFilterUpdates,
    }),
    dispatch => ({
        closeFilterDisplay: () => dispatch(toggleFilterDisplay(false)),
        openFilterDisplay: () => dispatch(toggleFilterDisplay(true)),
    })
)(BuyMobileNavComponent);

export const BuyMobileNav = createFragmentContainer(BuyMobileNavComponent, {
    viewer: graphql`
        fragment BuyMobileNav_viewer on Viewer {
            ...BuyMobileNavRefineMenuFilters_viewer
        }
    `,
    buyPage: graphql`
        fragment BuyMobileNav_buyPage on ItemSearchQueryConnection {
            totalResults
            appliedFilters {
                name
                values {
                    urlLabel
                }
                canBeDismissed
            }
            pageType
            ...SbMobileAppliedFilters_itemSearch
            ...BuyMobileNavRefineMenuFilters_itemSearch
            ...BuyMobuileRefineMenuSortBy_itemSearch
        }
    `,
    filters: graphql`
        fragment BuyMobileNav_filters on SearchBrowseFilter @relay(plural: true) {
            ...BuyMobileNavRefineMenuFilters_filters
        }
    `,
    user: graphql`
        fragment BuyMobileNav_user on User {
            ...SbMobileAppliedFilters_user
            ...BuyMobileNavRefineMenuFilters_user
        }
    `,
});
